import React, { useMemo } from 'react';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DataGrid } from 'components';
import { useQueryVariables, ColumnConfig, DataConnection } from 'hooks';
import { SeasonListPropsFragment, useSeasonsQuery } from 'schema/serverTypes';
import { DataGridProps } from 'components/DataGrid/types';
import { formatDate } from 'helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link: {
      color: theme.palette.primary.dark,
      fontWeight: 500,
    },
  })
);

const getDataKey = (season: SeasonListPropsFragment) => season.id;

export const SeasonsGrid = () => {
  const classes = useStyles();

  const config = useMemo(() => {
    const columns: ColumnConfig<SeasonListPropsFragment>[] = [
      {
        id: 'title',
        header: 'Название сезона',
        render: (season: SeasonListPropsFragment) => (
          <Link to={`/battle-pass/seasons/${season.id}`} className={classes.link}>
            {season.generalInformation.title}
          </Link>
        ),
      },
      {
        id: 'start',
        header: 'Дата начала',
        style: { width: '15%' },
        render: (season: SeasonListPropsFragment) => (
          <Typography variant="body2">{formatDate(season.generalInformation.start)}</Typography>
        ),
      },
      {
        id: 'end',
        header: 'Дата завершения',
        style: { width: '15%' },
        render: (season: SeasonListPropsFragment) => (
          <Typography variant="body2">{formatDate(season.generalInformation.end)}</Typography>
        ),
      },
      {
        id: 'currency',
        header: 'Название валюты',
        style: { width: '20%' },
        render: (season: SeasonListPropsFragment) => (
          <Typography variant="body2">{season.currency.name.nominativeSingular}</Typography>
        ),
      },
    ];
    return {
      columns,
      rowsPerPage: 20,
    };
  }, [classes.link]);

  const {
    onSort,
    onSearch,
    onPage,
    variables,
    extractPagedData,
    columns,
    sortDirection,
    sortBy,
  } = useQueryVariables<SeasonListPropsFragment>(config);

  const { data, loading, error } = useSeasonsQuery({
    variables,
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    toast.error('Ошибка загрузки сезонов');
    return null;
  }

  const entities = data?.seasons as DataConnection<SeasonListPropsFragment>;
  const { data: seasons, count, startCursor, endCursor, page, rowsPerPage } = extractPagedData(
    entities
  );

  const gridProps: DataGridProps<SeasonListPropsFragment> = {
    data: seasons,
    onSort,
    onPage,
    onSearch,
    columns,
    getDataKey,
    count,
    startCursor,
    endCursor,
    page,
    rowsPerPage,
    sortDirection,
    sortBy,
    loading,
  };

  return <DataGrid<SeasonListPropsFragment> {...gridProps} />;
};
